import { makeVar, useReactiveVar } from '@apollo/client';
import { useMemo } from 'react';

export type TabNewsActivity = Readonly<
  Record<string, Readonly<Record<string, boolean>>>
>;

const terminalTabNewsActivityVar = makeVar<TabNewsActivity>({});

export const nextTabNewsActivity = (
  prev: TabNewsActivity,
  tabId: string,
  widgetId: string,
  hasNewPosts: boolean,
): TabNewsActivity => {
  if (prev[tabId]?.[widgetId] === hasNewPosts) return prev;
  if (!hasNewPosts && prev[tabId]?.[widgetId] == null) return prev;

  return {
    ...prev,
    [tabId]: {
      ...prev[tabId],
      [widgetId]: hasNewPosts,
    },
  };
};

export const reportTerminalTabNewsActivity = (
  tabId: string,
  widgetId: string,
  hasNewPosts: boolean,
): void => {
  const prev = terminalTabNewsActivityVar();
  const next = nextTabNewsActivity(prev, tabId, widgetId, hasNewPosts);
  if (next === prev) return;
  terminalTabNewsActivityVar(next);
};

export const clearTerminalTabNewsActivity = (tabId: string): void => {
  const prev = terminalTabNewsActivityVar();
  if (!prev[tabId]) return;
  const next = { ...prev };
  delete next[tabId];
  terminalTabNewsActivityVar(next);
};

export const tabHasNewsActivity = (
  activity: TabNewsActivity,
  tabId: string,
): boolean => Object.values(activity[tabId] ?? {}).some(Boolean);

export const useTerminalTabNewsActivityIds = (): ReadonlySet<string> => {
  const activity = useReactiveVar(terminalTabNewsActivityVar);
  return useMemo(() => {
    const ids = new Set<string>();
    for (const tabId of Object.keys(activity)) {
      if (tabHasNewsActivity(activity, tabId)) ids.add(tabId);
    }
    return ids;
  }, [activity]);
};
